import React, { Suspense, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { newMenuItems, adminMenuItems } from "src/config/menuConfig";
import { Sider, Menu, MenuProps } from "src/assets/imports/antdComponents";
import { RootState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import { setConfirmationData, setUnsavedChanges } from "src/pages/ManageStudies/states/slice/unsavedChangesSlice";
import { setActiveSection, updateSections } from "src/pages/ManageStudies/states/slice/manageDescriptionGuideSlice";
import { useTranslation } from "react-i18next";
import { confirmationStatusType, confirmationTypes, FeatureKeysType, roleAccessType } from "src/pages/Studies/models/interfaces";
import ZSLogo from "public/icon/ZSLogo.svg?url";
import UserManagementFilled from "public/icon/UserManagement_fill.svg";
import UserManagement from "public/icon/UserManagement.svg";
import FeatureManage from "public/icon/feature_manage.svg";
import FeatureManageFilled from "public/icon/feature_manage_fill.svg";
import CustomTypography from "public/icon/CustomTypography.svg";
import CustomTypographyFilled from "public/icon/CustomTypography_fill.svg";
import ManageStudies from "public/icon/manageStudies.svg";
import ManageStudiesFilled from "public/icon/manageStudies_fill.svg";
import PatientExperienceBankIcon from "public/icon/patient_experience_bank_icon.svg";
import TaxonomyFilled from "public/icon/Taxonomy_fill.svg";
import Taxonomy from "public/icon/Taxonomy.svg";
import ConfirmationDialog from "src/components/ConfirmationDialog";
import { useFeatures } from "src/config/featureManager";
import { setVisibleCreateSegment } from "src/pages/Insights/states/quantSegmentSlice";
import { environment } from "src/config/environment";
import "./style.css";
const Help = React.lazy(() => import("src/pages/Help"));

const SideNavbar: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { isFeatureAvailable } = useFeatures();

	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const [key, setKey] = useState("");
	const { role, isNavDisabled } = useSelector((state: RootState) => state.studiesSlice);
	const { hasUnsavedChanges, confirmationData } = useSelector((state: RootState) => state.unsavedChangesSlice);
	const { selectedTheme } = useSelector((state: RootState) => state.themeManagementSlice);
	const initialConfirmationData = { status: "", editType: "", visible: false, name: "", redirectFrom: "" };
	const isNotesEnabled = isFeatureAvailable(FeatureKeysType.Notes);
	const isAskKuralEnabled = isFeatureAvailable(FeatureKeysType.AskKural);
	const isPersonasEnabled = isFeatureAvailable(FeatureKeysType.Personas);
	const isAdminTranscriptExportEnabled = isFeatureAvailable(FeatureKeysType.AdminTranscriptExport);

	const selectedKey = useMemo(() => {
		return role === roleAccessType.Admin
			? adminMenuItems.find((item) => item.path === location.pathname)?.key || "adminCenter"
			: newMenuItems.find((item) => item.path === location.pathname)?.key || "studies";
	}, [location.pathname, role]);

	const sidebarColor = getComputedStyle(document.documentElement).getPropertyValue("--sidebar-color").trim();
	const isSidebarColorWhite = sidebarColor === "#ffffff" || sidebarColor === "white" || sidebarColor === "#fff";

	const icons: { [key: string]: React.ReactNode } = useMemo(
		() => ({
			studies: <i className={`navicon material-symbols-outlined${selectedKey === "studies" ? " filled-icon" : ""}`}>home</i>,
			insights: <i className={`navicon material-symbols-outlined${selectedKey === "insights" ? " filled-icon" : ""}`}>insights</i>,
			manageStudies: <i className="navicon">{selectedKey === "manageStudies" ? <ManageStudiesFilled /> : <ManageStudies />}</i>,
			taxonomy: <i className="navicon">{selectedKey === "taxonomy" && isSidebarColorWhite ? <TaxonomyFilled /> : <Taxonomy />}</i>,
			manageQuant: <i className="navicon">{selectedKey === "manageQuant" && isSidebarColorWhite ? <TaxonomyFilled /> : <Taxonomy />}</i>,
			redirectToPage:
				environment.REACT_APP_REDIRECT_PAGE_URL !== undefined ? (
					<i className="navicon">
						<PatientExperienceBankIcon />
					</i>
				) : null,
			help: <i className="navicon material-symbols-outlined">help</i>,
		}),
		[selectedKey]
	);

	const adminIcons: { [key: string]: React.ReactNode } = useMemo(
		() => ({
			adminCenter: <i className="navicon">{selectedKey === "adminCenter" && isSidebarColorWhite ? <UserManagementFilled /> : <UserManagement />}</i>,
			featureManagement: <i className="navicon">{selectedKey === "featureManagement" && isSidebarColorWhite ? <FeatureManageFilled /> : <FeatureManage />}</i>,
			eventLogs: <i className={`navicon material-symbols-outlined${selectedKey === "eventLogs" && isSidebarColorWhite ? " filled-icon" : ""}`}>event_list</i>,
			transcriptExport: <i className={`navicon material-symbols-outlined${selectedKey === "transcriptExport" && isSidebarColorWhite ? " filled-icon" : ""}`}>event_list</i>,
			themeManagement: <i className="navicon">{selectedKey === "themeManagement" && isSidebarColorWhite ? <CustomTypographyFilled /> : <CustomTypography />}</i>,
			help: <i className="navicon material-symbols-outlined">help</i>,
		}),
		[selectedKey]
	);

	const handleClick = useCallback(
		(key: string, path?: string) => {
			if (isNavDisabled && key !== "help") {
				return;
			}

			if (key === "help") {
				setIsHelpModalOpen(true);
			} else if (key === "redirectToPage") {
				const redirectUrl = environment.REACT_APP_REDIRECT_PAGE_URL;
				if (redirectUrl) {
					window.open(redirectUrl, "_self");
				}
			} else if (hasUnsavedChanges) {
				dispatch(
					setConfirmationData({
						...confirmationData,
						redirectFrom: confirmationTypes.type3,
						status: confirmationStatusType.warning,
						visible: true,
					})
				);
				setKey(path || key);
			} else if (path) {
				navigate(path);
			} else {
				dispatch(setUnsavedChanges(false));
			}
		},
		[hasUnsavedChanges, isNavDisabled, navigate, dispatch, confirmationData]
	);

	const handleCancel = () => {
		setIsHelpModalOpen(false);
	};

	const onConfirm = () => {
		dispatch(setUnsavedChanges(false));
		dispatch(setActiveSection(null));
		dispatch(updateSections([]));
		navigate(key);
		if (confirmationData.editType === "segment") {
			dispatch(setVisibleCreateSegment(false));
		}
		dispatch(setConfirmationData(initialConfirmationData));
	};

	const onCancel = () => {
		dispatch(setConfirmationData({ ...confirmationData, visible: false }));
	};

	const menuItemsWithIcons: MenuProps["items"] = useMemo(() => {
		const menu = role === roleAccessType.Admin ? adminMenuItems : newMenuItems;
		const filteredMenu = menu
			.filter((item) => !(item as any).hideOnSidebar)
			.filter((item) => !(item.key === "redirectToPage" && !environment.REACT_APP_REDIRECT_PAGE_URL))
			.filter((item) => !(item.key === "taxonomy" && !isNotesEnabled))
			.filter((item) => !(item.key === "askKural" && !isAskKuralEnabled))
			.filter((item) => !(item.key === "personas" && !isPersonasEnabled))
			.filter((item) => !(item.key === "transcriptExport" && !isAdminTranscriptExportEnabled));

		const items = filteredMenu.map((item) => ({
			key: item.key,
			icon: role === roleAccessType.Admin ? adminIcons[item.key] : icons[item.key],
			label: item.label,
			className: `flex justify-center items-center py-8 ${
				item.key === "help" ? "menu-help" : item.key === "settings" ? "menu-settings" : item.key === "redirectToPage" ? "redirect-to-page" : ""
			}`,
			onClick: () => handleClick(item.key, item.path),
			title: t(item.label),
			disabled: isNavDisabled && item.key !== "help",
			style: isNavDisabled && item.key !== "help" ? { opacity: 0.5, cursor: "not-allowed" } : undefined,
		}));
		return items;
	}, [role, icons, hasUnsavedChanges, adminIcons, isNavDisabled, handleClick, isNotesEnabled, isAskKuralEnabled, isPersonasEnabled, isAdminTranscriptExportEnabled]);

	return (
		<>
			{/* {confirmationData?.redirectFrom === confirmationTypes.type3 && <ConfirmationDialog confirmationData={confirmationData} onCancel={onCancel} onConfirm={onConfirm} />} */}
			<Sider width={48} className={`left-navbar`}>
				{selectedTheme?.isSideNavbarPrimary && (
					<div className="flex items-center justify-center pt-1">
						<img src={String(ZSLogo)} alt="ZS Logo" className="m-2" style={{ maxWidth: "2rem" }} />
					</div>
				)}
				<Menu mode="inline" selectedKeys={[selectedKey]} className="h-fit" items={menuItemsWithIcons} />
			</Sider>
			<Suspense fallback={<></>}>{isHelpModalOpen && <Help key="help-modal" isModalOpen={isHelpModalOpen} handleOk={handleCancel} handleCancel={handleCancel} />}</Suspense>
		</>
	);
};

export default SideNavbar;
